'use client';
import * as React from 'react';

import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/alertDialog';
import { Button } from '../../components/button';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '../../components/sheet';
import { Textarea } from '../../components/textArea';
import { type AlertAction, AlertDialogContext } from './AlertDialogProvider.hooks';

interface AlertDialogState {
  open: boolean;
  title: string;
  body: React.ReactNode;
  type: 'alert' | 'confirm' | 'prompt';
  cancelButton: string;
  actionButton: string;
  destructive: boolean;
  defaultValue?: string;
  multiline?: boolean;
  inputProps?: React.TextareaHTMLAttributes<HTMLTextAreaElement>;
}

/** The prompt's field grows with its text from one line to this many, then scrolls. */
const PROMPT_GROWTH = { minRows: 1, maxRows: 10 };

function alertDialogReducer(state: AlertDialogState, action: AlertAction): AlertDialogState {
  switch (action.type) {
    case 'close':
      return { ...state, open: false };
    case 'alert':
    case 'confirm':
    case 'prompt':
      return {
        ...state,
        open: true,
        ...action,
        // if they are not passed - we should not keep old values
        body: action.body || '',
        title: action.title || '',
        cancelButton: action.cancelButton || (action.type === 'alert' ? 'Okay' : 'Cancel'),
        actionButton: ('actionButton' in action && action.actionButton) || 'Okay',
        destructive: ('destructive' in action && action.destructive) || false,
        defaultValue: 'defaultValue' in action ? action.defaultValue : undefined,
        multiline: ('multiline' in action && action.multiline) || false,
        inputProps: 'inputProps' in action ? action.inputProps : undefined,
      };
    default:
      return state;
  }
}

/**
 * `presentation` is how a confirmation, alert or prompt is shown: a centred alert dialog, or on
 * phones and tablets a bottom sheet with the question as its title, one line of consequence, the
 * primary (or destructive) action and a ghost Cancel. The app decides from its own layout; the
 * hooks (`useConfirm`, `useAlert`, `usePrompt`) are the same either way.
 */
export function AlertDialogProvider({
  children,
  presentation = 'dialog',
}: {
  children: React.ReactNode;
  presentation?: 'dialog' | 'sheet';
}) {
  const [state, dispatch] = React.useReducer(alertDialogReducer, {
    open: false,
    title: '',
    body: '',
    type: 'alert',
    cancelButton: 'Cancel',
    actionButton: 'Okay',
    destructive: false,
    multiline: false,
  });

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const resolveRef = React.useRef<(tf: any) => void>(undefined);

  function close() {
    dispatch({ type: 'close' });
    resolveRef.current?.(false);
  }

  function confirm(value?: string) {
    dispatch({ type: 'close' });
    resolveRef.current?.(value ?? true);
  }

  const dialog = React.useCallback(async <T extends AlertAction>(params: T) => {
    dispatch(params);

    return new Promise<T['type'] extends 'alert' | 'confirm' ? boolean : null | string>(
      (resolve) => {
        resolveRef.current = resolve;
      },
    );
  }, []);

  const onSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const field = event.currentTarget.elements.namedItem('prompt');
    confirm(field instanceof HTMLTextAreaElement ? field.value : undefined);
  };

  const actionVariant = state.destructive ? 'destructive' : 'default';

  if (presentation === 'sheet') {
    return (
      <AlertDialogContext value={dialog}>
        {children}
        <Sheet
          open={state.open}
          onOpenChange={(open) => {
            if (!open) close();
          }}
        >
          <SheetContent
            side="bottom"
            className="z-[99999] max-h-[calc(100dvh-3rem)] overscroll-contain rounded-t-xl pb-[calc(env(safe-area-inset-bottom)+1rem)]"
            closeButtonClassName="hidden"
          >
            <form className="flex flex-col gap-4" onSubmit={onSubmit}>
              <SheetHeader className="text-left">
                <div className="flex items-start justify-between gap-3">
                  <SheetTitle className="min-w-0 flex-1 pt-2">{state.title}</SheetTitle>
                  <Button
                    type="button"
                    variant="ghost"
                    className="-mr-3 -mt-1 shrink-0"
                    onClick={close}
                  >
                    Close
                  </Button>
                </div>
                {state.body ? <SheetDescription>{state.body}</SheetDescription> : null}
              </SheetHeader>
              {state.type === 'prompt' && (
                <Textarea
                  name="prompt"
                  defaultValue={state.defaultValue}
                  autoGrow={PROMPT_GROWTH}
                  singleLine={!state.multiline}
                  className="touch:text-base"
                  {...state.inputProps}
                />
              )}
              <div className="flex flex-col gap-2">
                {state.type === 'alert' ? null : (
                  <Button type="submit" variant={actionVariant} className="w-full">
                    {state.actionButton}
                  </Button>
                )}
                <Button type="button" variant="ghost" className="w-full" onClick={close}>
                  {state.cancelButton}
                </Button>
              </div>
            </form>
          </SheetContent>
        </Sheet>
      </AlertDialogContext>
    );
  }

  return (
    <AlertDialogContext value={dialog}>
      {children}
      <AlertDialog
        open={state.open}
        onOpenChange={(open) => {
          if (!open) close();
          return;
        }}
      >
        <AlertDialogContent className="z-[99999]" asChild>
          <form className="grid-cols-[minmax(0,1fr)]" onSubmit={onSubmit}>
            <AlertDialogHeader>
              <AlertDialogTitle>{state.title}</AlertDialogTitle>
              {state.body ? <AlertDialogDescription>{state.body}</AlertDialogDescription> : null}
            </AlertDialogHeader>
            {state.type === 'prompt' && (
              <Textarea
                name="prompt"
                defaultValue={state.defaultValue}
                autoGrow={PROMPT_GROWTH}
                singleLine={!state.multiline}
                className="touch:text-base"
                {...state.inputProps}
              />
            )}
            <AlertDialogFooter className="gap-2">
              <Button variant="secondary" type="button" onClick={close}>
                {state.cancelButton}
              </Button>
              {state.type === 'alert' ? null : (
                <Button type="submit" variant={actionVariant}>
                  {state.actionButton}
                </Button>
              )}
            </AlertDialogFooter>
          </form>
        </AlertDialogContent>
      </AlertDialog>
    </AlertDialogContext>
  );
}
